import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { ShieldAlert, ExternalLink, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable } from './shared';

const SEV_BADGE = {
  Critical: 'bg-red-400/15 text-red-300 border-red-400/30',
  Warning: 'bg-amber-400/15 text-amber-300 border-amber-400/30',
  Info: 'bg-slate-400/15 text-slate-200 border-slate-400/30',
};

const DOC_ROUTE = {
  invoice: (id) => `/invoices/${id}`,
  payment: (id, inv) => inv ? `/invoices/${inv}` : `/payments`,
  quotation: (id) => `/quotations/${id}`,
};

export function AuditTab({ data, loading, filters, onRefresh, includeReviewed, setIncludeReviewed }) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const s = data?.summary || {};
  const cards = [
    { label: 'Total Issues', value: s.total_issues || 0, accent: s.total_issues > 0 ? 'warn' : 'success' },
    { label: 'Critical', value: s.critical || 0, accent: 'danger' },
    { label: 'Warning', value: s.warning || 0, accent: 'warn' },
    { label: 'Info', value: s.info || 0, accent: '' },
    { label: 'Missing Documents', value: s.missing_documents || 0, accent: 'warn' },
    { label: 'Tax Issues', value: s.tax_issues || 0, accent: 'warn' },
    { label: 'Payment Issues', value: s.payment_issues || 0, accent: 'danger' },
  ];
  const rows = data?.rows || [];

  const markReviewed = async (issue) => {
    setBusyId(issue.issue_id);
    try {
      await api.post(`/reports/v2/audit/${issue.issue_id}/review`);
      toast.success('Issue ditandai sudah direview');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal mark reviewed');
    } finally {
      setBusyId(null);
    }
  };

  const unmark = async (issue) => {
    setBusyId(issue.issue_id);
    try {
      await api.delete(`/reports/v2/audit/${issue.issue_id}/review`);
      toast.success('Tanda review dihapus');
      onRefresh && onRefresh();
    } catch (e) {
      toast.error('Gagal');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <SectionHeader
        icon={ShieldAlert} title="Audit Data"
        action={
          <div className="inline-flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-slate-300">
              <Switch checked={!!includeReviewed} onCheckedChange={(v) => setIncludeReviewed && setIncludeReviewed(v)} data-testid="audit-include-reviewed" />
              Tampilkan yang sudah direview
            </label>
            <ExportButtons tab="audit" filters={filters} />
          </div>
        }
      />
      <KpiGrid items={cards} loading={loading} columns={4} />
      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Daftar Issues ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.issue_id}
            columns={[
              { key: 'severity', label: 'Severity', render: r => <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${SEV_BADGE[r.severity]}`}>{r.severity}</span> },
              { key: 'issue_type', label: 'Issue', render: r => <span className="font-medium text-slate-100">{r.issue_type}</span> },
              { key: 'doc_type', label: 'Dokumen', render: r => <span className="text-xs text-slate-300 uppercase">{r.doc_type}</span> },
              { key: 'doc_label', label: 'Nomor / Label', render: r => <span className="font-mono-num text-slate-200">{r.doc_label}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="text-slate-300">{r.client_name}</span> },
              { key: 'description', label: 'Description', render: r => <span className="text-xs text-slate-300">{r.description}</span> },
              { key: 'suggested_fix', label: 'Suggested Fix', render: r => <span className="text-xs text-slate-400">{r.suggested_fix}</span> },
              { key: 'reviewed', label: 'Reviewed', render: r => r.reviewed ? <span className="text-xs text-emerald-300 font-medium" title={`oleh ${r.reviewed_by_name}`}>Ya</span> : <span className="text-xs text-slate-400">Belum</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs"
                    onClick={() => navigate((DOC_ROUTE[r.doc_type] || (() => '/'))(r.doc_id))}
                    data-testid={`audit-open-${r.issue_id}`}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                  {r.reviewed ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-slate-300"
                      onClick={() => unmark(r)} disabled={busyId === r.issue_id}
                      data-testid={`audit-unmark-${r.issue_id}`}
                    >Undo</Button>
                  ) : (
                    <Button size="sm" className="h-7 px-2 text-xs"
                      onClick={() => markReviewed(r)} disabled={busyId === r.issue_id}
                      data-testid={`audit-mark-${r.issue_id}`}
                    >
                      <Check className="h-3 w-3 mr-1" /> Reviewed
                    </Button>
                  )}
                </div>
              ) },
            ]}
            rows={rows}
            emptyText="Tidak ditemukan masalah data."
            mobileCard={(r) => (
              <div className="text-sm space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${SEV_BADGE[r.severity]}`}>{r.severity}</span>
                      <span className="text-xs uppercase text-slate-400">{r.doc_type}</span>
                    </div>
                    <div className="font-semibold text-slate-100 mt-1">{r.issue_type}</div>
                  </div>
                  {r.reviewed && <span className="text-xs text-emerald-300 font-medium">✓</span>}
                </div>
                <div className="text-xs text-slate-300">{r.client_name} • <span className="font-mono-num text-slate-200">{r.doc_label}</span></div>
                <div className="text-xs text-slate-400">{r.description}</div>
                <div className="text-xs text-slate-400 italic">Fix: {r.suggested_fix}</div>
                <div className="flex gap-1 pt-1">
                  <Button size="sm" variant="secondary" className="h-7 px-2 text-xs"
                    onClick={() => navigate((DOC_ROUTE[r.doc_type] || (() => '/'))(r.doc_id))}
                  >Buka</Button>
                  {r.reviewed ? (
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => unmark(r)}>Undo</Button>
                  ) : (
                    <Button size="sm" className="h-7 px-2 text-xs" onClick={() => markReviewed(r)}>Mark Reviewed</Button>
                  )}
                </div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
